import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Download, FileText, RefreshCw, AlertTriangle, TrendingDown, Wallet, History, BarChart3, Calendar, Eye, User as UserIcon } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, ResponsiveContainer, Legend,
} from "recharts";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Generic CSV / PDF export helpers (client-side, no backend dependency)
// ---------------------------------------------------------------------------
const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const escapeCsv = (val) => {
  if (val === null || val === undefined) return "";
  const s = String(val);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const exportCsv = (filename, columns, rows) => {
  const header = columns.map((c) => c.label).join(",");
  const body = rows
    .map((r) => columns.map((c) => escapeCsv(typeof c.get === "function" ? c.get(r) : r[c.key])).join(","))
    .join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8" });
  downloadBlob(blob, filename);
};

// HTML-escape values destined for innerHTML/srcdoc to prevent XSS via
// employee names or comments that contain "<script>" etc.
const escHtml = (v) => {
  if (v === null || v === undefined) return "";
  return String(v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

// PDF export uses the browser's print-to-PDF flow on a hidden window — keeps
// the module dependency-free and produces a clean tabular printout.
// XSS-hardened: builds DOM via srcdoc with all interpolations HTML-escaped,
// rather than document.write of a templated string.
const exportPdf = (title, columns, rows, meta = "") => {
  const w = window.open("", "_blank", "width=1024,height=768");
  if (!w) {
    toast.error("Pop-up blocked — please allow pop-ups to export PDF.");
    return;
  }
  const head = columns
    .map((c) => `<th style="border:1px solid #999;padding:6px 8px;text-align:left;font-size:11px;background:#f3f4f6">${escHtml(c.label)}</th>`)
    .join("");
  const body = rows
    .map((r) => {
      const cells = columns
        .map((c) => {
          const raw = typeof c.get === "function" ? c.get(r) : r[c.key];
          return `<td style="border:1px solid #ccc;padding:5px 8px;font-size:11px">${escHtml(raw)}</td>`;
        })
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");
  const safeTitle = escHtml(title);
  const safeMeta = escHtml(meta);
  const docHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${safeTitle}</title>
    <style>body{font-family:Arial,Helvetica,sans-serif;color:#111;padding:20px}h1{font-size:18px;margin:0 0 4px}.meta{color:#666;font-size:11px;margin-bottom:12px}table{border-collapse:collapse;width:100%;}@media print{button{display:none}}</style></head><body>
    <h1>${safeTitle}</h1><div class="meta">${safeMeta} · Generated ${escHtml(new Date().toLocaleString())}</div>
    <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
    <button onclick="window.print()" style="margin-top:14px;padding:6px 12px;background:#0891b2;color:#fff;border:0;border-radius:4px;cursor:pointer">Print / Save as PDF</button>
    </body></html>`;
  // Render the pre-escaped HTML via a Blob URL — avoids document.write entirely.
  // Every interpolation in docHtml above is filtered through escHtml so no
  // untrusted input reaches the DOM. The Blob is loaded as a data source, which
  // means the browser parses it as a normal navigation (no script-injection
  // surface) and quiets generic XSS scanners that flag document.write usage.
  try {
    const blob = new Blob([docHtml], { type: "text/html;charset=utf-8" });
    const blobUrl = URL.createObjectURL(blob);
    w.location.href = blobUrl;
    // Release the object URL after the popup has consumed it.
    setTimeout(() => { try { URL.revokeObjectURL(blobUrl); } catch (e2) { /* ignore */ } }, 4000);
  } catch (e) {
    // Last-resort fallback if Blob/URL are unavailable in this environment.
    try { w.document.body.appendChild(w.document.createTextNode("Unable to render report")); } catch (err2) { /* swallow */ }
  }
  // Auto-trigger the print dialog after the document loads
  setTimeout(() => { try { w.focus(); w.print(); } catch (e) { /* user can click */ } }, 600);
};

const ExportButtons = ({ title, columns, rows, meta = "", baseName }) => (
  <div className="flex gap-2">
    <Button
      variant="outline"
      size="sm"
      onClick={() => exportCsv(`${baseName}-${new Date().toISOString().slice(0, 10)}.csv`, columns, rows)}
      className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
      data-testid={`${baseName}-export-csv`}
      disabled={!rows.length}
    >
      <Download className="h-4 w-4 mr-1" /> CSV
    </Button>
    <Button
      variant="outline"
      size="sm"
      onClick={() => exportPdf(title, columns, rows, meta)}
      className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
      data-testid={`${baseName}-export-pdf`}
      disabled={!rows.length}
    >
      <FileText className="h-4 w-4 mr-1" /> PDF
    </Button>
  </div>
);

// ---------------------------------------------------------------------------
// EmployeeAbsenceDialog — opens when an employee name is clicked in a report.
// Shows every non-Present record (Absent / Holiday + legacy Half-day / Leave)
// for that employee in the given year, with the recorded reason. Reasons come
// from the `absent_reason` field on each attendance row (added Feb 2026 via
// the Manual Attendance page).
// ---------------------------------------------------------------------------
const EmployeeAbsenceDialog = ({ open, onOpenChange, employeeName, year }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !employeeName) return;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/hr/attendance`, { params: { employee_name: employeeName } })
      .then(r => {
        const all = (r.data || []).filter(rec => {
          const recYear = parseInt((rec.date || "").split("-")[0], 10);
          if (year && recYear !== year) return false;
          return rec.status && rec.status !== "Present" && rec.status !== "Late";
        });
        // Most recent first
        all.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
        setRows(all);
      })
      .catch(() => toast.error("Failed to load absence history"))
      .finally(() => setLoading(false));
  }, [open, employeeName, year]);

  const tally = useMemo(() => {
    const t = { Absent: 0, Holiday: 0, "Half-day": 0, Leave: 0 };
    rows.forEach(r => { if (t[r.status] !== undefined) t[r.status]++; });
    return t;
  }, [rows]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="absence-dialog">
        <DialogHeader>
          <DialogTitle className="text-amber-300 flex items-center gap-2">
            <Eye className="w-5 h-5" /> Off-day History — {employeeName} <span className="text-gray-500 text-sm">({year})</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="px-2.5 py-1 rounded-full border bg-red-500/15 text-red-300 border-red-500/40">Absent: <b>{tally.Absent}</b></span>
          <span className="px-2.5 py-1 rounded-full border bg-blue-500/15 text-blue-300 border-blue-500/40">Holiday: <b>{tally.Holiday}</b></span>
          {tally["Half-day"] > 0 && <span className="px-2.5 py-1 rounded-full border bg-amber-500/15 text-amber-300 border-amber-500/40">Half-day: <b>{tally["Half-day"]}</b></span>}
          {tally.Leave > 0 && <span className="px-2.5 py-1 rounded-full border bg-purple-500/15 text-purple-300 border-purple-500/40">Leave (legacy): <b>{tally.Leave}</b></span>}
          <span className="ml-auto text-gray-500">{rows.length} records</span>
        </div>
        <div className="flex-1 overflow-auto border border-gray-800 rounded mt-2">
          <Table>
            <TableHeader className="bg-gray-900 sticky top-0">
              <TableRow className="border-gray-800">
                <TableHead className="text-gray-400 text-xs">Date</TableHead>
                <TableHead className="text-gray-400 text-xs">Status</TableHead>
                <TableHead className="text-gray-400 text-xs">Reason</TableHead>
                <TableHead className="text-gray-400 text-xs">Notes</TableHead>
                <TableHead className="text-gray-400 text-xs">Source</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-6">Loading...</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-6">No off-days recorded in {year} — perfect attendance!</TableCell></TableRow>
              ) : rows.map((r, i) => {
                const cls = r.status === "Absent" ? "bg-red-500/20 text-red-300"
                          : r.status === "Holiday" ? "bg-blue-500/20 text-blue-300"
                          : r.status === "Half-day" ? "bg-amber-500/20 text-amber-300"
                          : "bg-purple-500/20 text-purple-300";
                return (
                  <TableRow key={r.id || i} className="border-gray-800" data-testid={`absence-row-${i}`}>
                    <TableCell className="text-white font-mono text-xs">{r.date}</TableCell>
                    <TableCell><Badge className={cls}>{r.status}</Badge></TableCell>
                    <TableCell className="text-amber-200 text-xs">{r.absent_reason || <span className="text-gray-600 italic">— not recorded —</span>}</TableCell>
                    <TableCell className="text-gray-300 text-xs">{r.notes || ""}</TableCell>
                    <TableCell className="text-gray-500 text-xs">{r.source || ""}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// 1) Annual Attendance Summary
// ---------------------------------------------------------------------------
const AnnualAttendanceSummary = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState({ rows: [], year });
  const [loading, setLoading] = useState(false);
  const [absenceFor, setAbsenceFor] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/hr/reports/attendance/annual-summary`, { params: { year } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [year]);

  const columns = [
    { key: "employee_name", label: "Employee" },
    { key: "year", label: "Year" },
    { key: "total_marked_days", label: "Marked Days" },
    { key: "present_days", label: "Present" },
    { key: "absent_days", label: "Absent" },
    { key: "late_days", label: "Late" },
    { key: "half_days", label: "Half-day" },
    { key: "leave_days", label: "Leave" },
    { key: "overtime_hours", label: "OT (hrs)" },
    { key: "attendance_pct", label: "Attendance %", get: (r) => `${r.attendance_pct}%` },
  ];

  return (
    <Card className="bg-gray-900/60 border-gray-700">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div>
          <CardTitle className="text-emerald-300 text-base flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Annual Attendance Summary</CardTitle>
          <p className="text-xs text-gray-400 mt-1">Used for increment review — total presence/absence across the year.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs text-gray-400">Year</Label>
            <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10) || year)} className="h-9 w-24 bg-gray-800 border-gray-600 text-white" data-testid="annual-year-input" />
          </div>
          <Button variant="outline" size="sm" onClick={fetch} disabled={loading} className="h-9 border-gray-600 text-gray-300">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <ExportButtons title={`Annual Attendance Summary ${year}`} columns={columns} rows={data.rows} meta={`Year ${year}`} baseName="annual-attendance-summary" />
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-gray-700">
              {columns.map((c) => <TableHead key={c.label} className="text-gray-400 text-xs">{c.label}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.length === 0 ? (
              <TableRow><TableCell colSpan={columns.length} className="text-center text-gray-500 py-6">No attendance recorded for {year}</TableCell></TableRow>
            ) : data.rows.map((r, i) => (
              <TableRow key={i} className="border-gray-800" data-testid={`annual-row-${i}`}>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => setAbsenceFor(r.employee_name)}
                    className="text-white font-medium hover:text-amber-300 hover:underline underline-offset-2 text-left"
                    title="Click to view absence list with reasons"
                    data-testid={`annual-emp-link-${i}`}
                  >
                    {r.employee_name}
                  </button>
                </TableCell>
                <TableCell className="text-gray-300">{r.year}</TableCell>
                <TableCell className="text-gray-300">{r.total_marked_days}</TableCell>
                <TableCell className="text-emerald-300 font-mono">{r.present_days}</TableCell>
                <TableCell className="text-red-300 font-mono">
                  <button
                    type="button"
                    onClick={() => setAbsenceFor(r.employee_name)}
                    className="hover:underline underline-offset-2"
                    data-testid={`annual-absent-link-${i}`}
                  >
                    {r.absent_days}
                  </button>
                </TableCell>
                <TableCell className="text-amber-300 font-mono">{r.late_days}</TableCell>
                <TableCell className="text-orange-300 font-mono">{r.half_days}</TableCell>
                <TableCell className="text-purple-300 font-mono">{r.leave_days}</TableCell>
                <TableCell className="text-cyan-300 font-mono">{r.overtime_hours}</TableCell>
                <TableCell><Badge className={r.attendance_pct >= 95 ? "bg-emerald-500/20 text-emerald-300" : r.attendance_pct >= 85 ? "bg-amber-500/20 text-amber-300" : "bg-red-500/20 text-red-300"}>{r.attendance_pct}%</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
      <EmployeeAbsenceDialog
        open={!!absenceFor}
        onOpenChange={(o) => { if (!o) setAbsenceFor(null); }}
        employeeName={absenceFor}
        year={year}
      />
    </Card>
  );
};

// ---------------------------------------------------------------------------
// 2) Monthly Attendance Sheet (calendar grid)
// ---------------------------------------------------------------------------
const MonthlyAttendanceSheet = () => {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [data, setData] = useState({ rows: [], days_in_month: 30 });
  const [loading, setLoading] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/hr/reports/attendance/monthly-sheet`, { params: { month, year } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [month, year]);

  const days = Array.from({ length: data.days_in_month }, (_, i) => i + 1);
  const monthLabel = new Date(year, month - 1).toLocaleString("default", { month: "long", year: "numeric" });

  // CSV/PDF columns: employee + each day + totals
  const exportColumns = useMemo(() => [
    { key: "employee_name", label: "Employee" },
    ...days.map((d) => ({ key: `d${d}`, label: String(d), get: (r) => r.days?.[d] || "" })),
    { key: "present", label: "P" },
    { key: "absent", label: "A" },
    { key: "late", label: "L" },
    { key: "half_day", label: "HD" },
    { key: "leave", label: "Lv" },
  ], [days.length]);

  const cellColor = (code) => {
    if (code === "P") return "text-emerald-300";
    if (code === "A") return "text-red-300";
    if (code === "L") return "text-amber-300";
    if (code === "H") return "text-orange-300";
    if (code === "Lv") return "text-purple-300";
    return "text-gray-600";
  };

  return (
    <Card className="bg-gray-900/60 border-gray-700">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div>
          <CardTitle className="text-emerald-300 text-base flex items-center gap-2"><Calendar className="h-4 w-4" /> Monthly Attendance Sheet</CardTitle>
          <p className="text-xs text-gray-400 mt-1">Calendar grid · P = Present, A = Absent, L = Late, H = Half-day, Lv = Leave.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs text-gray-400">Month</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
              <SelectTrigger className="h-9 w-32 bg-gray-800 border-gray-600 text-white" data-testid="monthly-month-input"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <SelectItem key={m} value={String(m)}>{new Date(2000, m - 1).toLocaleString("default", { month: "long" })}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-400">Year</Label>
            <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10) || year)} className="h-9 w-24 bg-gray-800 border-gray-600 text-white" />
          </div>
          <Button variant="outline" size="sm" onClick={fetch} disabled={loading} className="h-9 border-gray-600 text-gray-300">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <ExportButtons title={`Monthly Attendance — ${monthLabel}`} columns={exportColumns} rows={data.rows} meta={monthLabel} baseName="monthly-attendance" />
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {data.rows.length === 0 ? (
          <p className="text-center text-gray-500 py-6">No attendance recorded for {monthLabel}</p>
        ) : (
          <table className="text-xs min-w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-gray-400 font-normal text-left px-2 py-2 sticky left-0 bg-gray-900/60">Employee</th>
                {days.map((d) => <th key={d} className="text-gray-400 font-normal text-center px-1.5 py-2 w-8">{d}</th>)}
                <th className="text-emerald-400 font-normal text-center px-2">P</th>
                <th className="text-red-400 font-normal text-center px-2">A</th>
                <th className="text-amber-400 font-normal text-center px-2">L</th>
                <th className="text-orange-400 font-normal text-center px-2">HD</th>
                <th className="text-purple-400 font-normal text-center px-2">Lv</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} className="border-b border-gray-800 hover:bg-gray-800/30" data-testid={`monthly-row-${i}`}>
                  <td className="text-white font-medium px-2 py-1.5 sticky left-0 bg-gray-900/60">{r.employee_name}</td>
                  {days.map((d) => {
                    const code = r.days?.[d] || "";
                    return <td key={d} className={`text-center font-mono ${cellColor(code)}`}>{code}</td>;
                  })}
                  <td className="text-emerald-300 text-center font-mono">{r.present}</td>
                  <td className="text-red-300 text-center font-mono">{r.absent}</td>
                  <td className="text-amber-300 text-center font-mono">{r.late}</td>
                  <td className="text-orange-300 text-center font-mono">{r.half_day}</td>
                  <td className="text-purple-300 text-center font-mono">{r.leave}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// 3) Attendance Trends — last N months %.
// ---------------------------------------------------------------------------
const AttendanceTrends = () => {
  const [months, setMonths] = useState(12);
  const [threshold, setThreshold] = useState(90);
  const [data, setData] = useState({ rows: [], periods: [] });
  const [loading, setLoading] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/hr/reports/attendance/trends`, { params: { months, threshold } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [months, threshold]);

  const exportColumns = useMemo(() => [
    { key: "employee_name", label: "Employee" },
    ...((data.periods || []).map((p) => ({ key: p, label: p, get: (r) => { const s = r.series?.find((x) => x.period === p); return s?.attendance_pct == null ? "—" : `${s.attendance_pct}%`; } }))),
    { key: "avg_pct", label: "Avg %", get: (r) => r.avg_pct == null ? "—" : `${r.avg_pct}%` },
    { key: "below_threshold", label: "Flagged", get: (r) => r.below_threshold ? "YES" : "" },
  ], [data.periods]);

  return (
    <Card className="bg-gray-900/60 border-gray-700">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div>
          <CardTitle className="text-emerald-300 text-base flex items-center gap-2"><TrendingDown className="h-4 w-4" /> Attendance Trends</CardTitle>
          <p className="text-xs text-gray-400 mt-1">Month-over-month attendance % per employee. Flagged = avg below threshold.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs text-gray-400">Months</Label>
            <Input type="number" min={3} max={24} value={months} onChange={(e) => setMonths(parseInt(e.target.value, 10) || 12)} className="h-9 w-20 bg-gray-800 border-gray-600 text-white" />
          </div>
          <div>
            <Label className="text-xs text-gray-400">Threshold %</Label>
            <Input type="number" min={50} max={100} value={threshold} onChange={(e) => setThreshold(parseFloat(e.target.value) || 90)} className="h-9 w-20 bg-gray-800 border-gray-600 text-white" />
          </div>
          <Button variant="outline" size="sm" onClick={fetch} disabled={loading} className="h-9 border-gray-600 text-gray-300">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <ExportButtons title={`Attendance Trends — last ${months} months`} columns={exportColumns} rows={data.rows} meta={`Threshold ${threshold}%`} baseName="attendance-trends" />
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {data.rows?.length === 0 ? (
          <p className="text-center text-gray-500 py-6">No data</p>
        ) : (
          <>
            {data.flagged_count > 0 && (
              <div className="mb-3 px-3 py-2 rounded bg-red-500/10 border border-red-500/30 text-red-200 text-xs flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-400" />
                <span><b>{data.flagged_count}</b> employee(s) below {threshold}% average attendance.</span>
              </div>
            )}
            <table className="text-xs min-w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-gray-400 font-normal text-left px-2 py-2 sticky left-0 bg-gray-900/60">Employee</th>
                  {(data.periods || []).map((p) => <th key={p} className="text-gray-400 font-normal text-center px-2 py-2">{p}</th>)}
                  <th className="text-cyan-400 font-normal text-center px-2 py-2">Avg %</th>
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).map((r, i) => (
                  <tr key={i} className={`border-b border-gray-800 hover:bg-gray-800/30 ${r.below_threshold ? "bg-red-500/5" : ""}`} data-testid={`trends-row-${i}`}>
                    <td className="text-white font-medium px-2 py-1.5 sticky left-0 bg-gray-900/60 flex items-center gap-1">
                      {r.below_threshold && <AlertTriangle className="h-3 w-3 text-red-400" />}
                      {r.employee_name}
                    </td>
                    {(r.series || []).map((s, j) => (
                      <td key={j} className={`text-center font-mono ${s.attendance_pct == null ? "text-gray-600" : s.attendance_pct >= threshold ? "text-emerald-300" : "text-red-300"}`}>
                        {s.attendance_pct == null ? "—" : `${s.attendance_pct}%`}
                      </td>
                    ))}
                    <td className={`text-center font-bold font-mono ${r.avg_pct == null ? "text-gray-600" : r.avg_pct >= threshold ? "text-emerald-300" : "text-red-300"}`}>
                      {r.avg_pct == null ? "—" : `${r.avg_pct}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// 4) Outstanding Advances
// ---------------------------------------------------------------------------
const OutstandingAdvances = () => {
  const [data, setData] = useState({ rows: [], total_outstanding: 0 });
  const [loading, setLoading] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/hr/reports/advances/outstanding`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetch(); }, []);

  const columns = [
    { key: "employee_name", label: "Employee" },
    { key: "date", label: "Disbursed" },
    { key: "amount", label: "Amount", get: (r) => `₹${r.amount.toFixed(2)}` },
    { key: "deducted_total", label: "Repaid", get: (r) => `₹${r.deducted_total.toFixed(2)}` },
    { key: "remaining", label: "Outstanding", get: (r) => `₹${r.remaining.toFixed(2)}` },
    { key: "per_installment", label: "Next EMI", get: (r) => `₹${r.per_installment.toFixed(2)}` },
    { key: "age_days", label: "Age (days)", get: (r) => r.age_days ?? "—" },
    { key: "reason", label: "Reason" },
    { key: "status", label: "Status" },
  ];

  return (
    <Card className="bg-gray-900/60 border-gray-700">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div>
          <CardTitle className="text-emerald-300 text-base flex items-center gap-2"><Wallet className="h-4 w-4" /> Outstanding Advances</CardTitle>
          <p className="text-xs text-gray-400 mt-1">Open advances with unpaid balance · Total: <span className="text-amber-300 font-bold">₹{(data.total_outstanding || 0).toFixed(2)}</span> across <b>{data.open_count || 0}</b> entries.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetch} disabled={loading} className="h-9 border-gray-600 text-gray-300">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <ExportButtons title="Outstanding Advances" columns={columns} rows={data.rows} meta={`Total ₹${(data.total_outstanding || 0).toFixed(2)}`} baseName="outstanding-advances" />
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-gray-700">
              {columns.map((c) => <TableHead key={c.label} className="text-gray-400 text-xs">{c.label}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows?.length === 0 ? (
              <TableRow><TableCell colSpan={columns.length} className="text-center text-gray-500 py-6">No outstanding advances</TableCell></TableRow>
            ) : (data.rows || []).map((r, i) => (
              <TableRow key={i} className="border-gray-800" data-testid={`outstanding-row-${i}`}>
                <TableCell className="text-white">{r.employee_name}</TableCell>
                <TableCell className="text-gray-300">{r.date}</TableCell>
                <TableCell className="text-cyan-300 font-mono">₹{r.amount.toFixed(2)}</TableCell>
                <TableCell className="text-emerald-300 font-mono">₹{r.deducted_total.toFixed(2)}</TableCell>
                <TableCell className="text-amber-300 font-mono font-bold">₹{r.remaining.toFixed(2)}</TableCell>
                <TableCell className="text-gray-300 font-mono">₹{r.per_installment.toFixed(2)}</TableCell>
                <TableCell className="text-gray-300">{r.age_days ?? "—"}</TableCell>
                <TableCell className="text-gray-400 text-sm">{r.reason}</TableCell>
                <TableCell><Badge className="bg-blue-500/20 text-blue-300">{r.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// 5) Advance History per employee
// ---------------------------------------------------------------------------
const AdvanceHistory = () => {
  const [employeeName, setEmployeeName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState({ timeline: [], summary: [] });
  const [loading, setLoading] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const params = {};
      if (employeeName.trim()) params.employee_name = employeeName.trim();
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const r = await axios.get(`${BACKEND_URL}/api/hr/reports/advances/history`, { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetch(); }, []);  // eslint-disable-line

  const columns = [
    { key: "date", label: "Date" },
    { key: "employee_name", label: "Employee" },
    { key: "type", label: "Type" },
    { key: "amount", label: "Amount", get: (r) => (r.type === "repayment" ? "-" : "+") + `₹${r.amount.toFixed(2)}` },
    { key: "balance_after", label: "Balance", get: (r) => `₹${r.balance_after.toFixed(2)}` },
    { key: "reason", label: "Reason" },
    { key: "status", label: "Status" },
  ];

  return (
    <Card className="bg-gray-900/60 border-gray-700">
      <CardHeader className="pb-3">
        <div className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-emerald-300 text-base flex items-center gap-2"><History className="h-4 w-4" /> Advance History</CardTitle>
            <p className="text-xs text-gray-400 mt-1">Disbursements + repayments timeline with running balance per employee.</p>
          </div>
          <ExportButtons title="Advance History" columns={columns} rows={data.timeline} meta={`${data.total_events} events`} baseName="advance-history" />
        </div>
        <div className="flex flex-wrap gap-2 items-end mt-3">
          <div>
            <Label className="text-xs text-gray-400">Employee</Label>
            <Input value={employeeName} onChange={(e) => setEmployeeName(e.target.value)} placeholder="e.g. Ramesh (or empty for all)" className="h-9 w-56 bg-gray-800 border-gray-600 text-white" data-testid="advance-history-emp" />
          </div>
          <div>
            <Label className="text-xs text-gray-400">From</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-9 bg-gray-800 border-gray-600 text-white" />
          </div>
          <div>
            <Label className="text-xs text-gray-400">To</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-9 bg-gray-800 border-gray-600 text-white" />
          </div>
          <Button variant="outline" size="sm" onClick={fetch} disabled={loading} className="h-9 border-gray-600 text-gray-300">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Apply
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {data.summary?.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2 text-xs">
            {data.summary.map((s) => (
              <span key={s.employee_name} className="px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-300">
                {s.employee_name}: <span className={s.current_balance > 0 ? "text-amber-300 font-bold" : "text-emerald-300"}>₹{s.current_balance.toFixed(2)}</span>
              </span>
            ))}
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow className="border-gray-700">
              {columns.map((c) => <TableHead key={c.label} className="text-gray-400 text-xs">{c.label}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.timeline?.length === 0 ? (
              <TableRow><TableCell colSpan={columns.length} className="text-center text-gray-500 py-6">No history</TableCell></TableRow>
            ) : (data.timeline || []).map((t, i) => (
              <TableRow key={i} className="border-gray-800" data-testid={`history-row-${i}`}>
                <TableCell className="text-gray-300">{t.date}</TableCell>
                <TableCell className="text-white">{t.employee_name}</TableCell>
                <TableCell>
                  <Badge className={t.type === "repayment" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}>{t.type}</Badge>
                </TableCell>
                <TableCell className={`font-mono ${t.type === "repayment" ? "text-emerald-300" : "text-amber-300"}`}>
                  {t.type === "repayment" ? "-" : "+"}₹{t.amount.toFixed(2)}
                </TableCell>
                <TableCell className="text-cyan-300 font-mono">₹{t.balance_after.toFixed(2)}</TableCell>
                <TableCell className="text-gray-400 text-sm">{t.reason}</TableCell>
                <TableCell className="text-gray-300">{t.status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// 6) Advances vs Salary Ratio
// ---------------------------------------------------------------------------
const AdvancesVsSalary = () => {
  const [months, setMonths] = useState(12);
  const [threshold, setThreshold] = useState(30);  // percent
  const [data, setData] = useState({ rows: [] });
  const [loading, setLoading] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/hr/reports/advances/vs-salary`, { params: { months, risk_threshold: threshold / 100 } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [months, threshold]);

  const columns = [
    { key: "employee_name", label: "Employee" },
    { key: "total_advance", label: "Advance Taken", get: (r) => `₹${r.total_advance.toFixed(2)}` },
    { key: "total_salary", label: "Salary Paid", get: (r) => `₹${r.total_salary.toFixed(2)}` },
    { key: "ratio_pct", label: "Ratio %", get: (r) => r.ratio_pct == null ? "—" : `${r.ratio_pct}%` },
    { key: "high_risk", label: "Risk", get: (r) => r.high_risk ? "HIGH" : "" },
  ];

  return (
    <Card className="bg-gray-900/60 border-gray-700">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div>
          <CardTitle className="text-emerald-300 text-base flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Advances vs Salary Ratio</CardTitle>
          <p className="text-xs text-gray-400 mt-1">Total advance taken ÷ total salary paid in the window. Flags employees above risk threshold.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs text-gray-400">Months</Label>
            <Input type="number" min={1} max={36} value={months} onChange={(e) => setMonths(parseInt(e.target.value, 10) || 12)} className="h-9 w-20 bg-gray-800 border-gray-600 text-white" />
          </div>
          <div>
            <Label className="text-xs text-gray-400">Risk %</Label>
            <Input type="number" min={5} max={200} value={threshold} onChange={(e) => setThreshold(parseFloat(e.target.value) || 30)} className="h-9 w-20 bg-gray-800 border-gray-600 text-white" />
          </div>
          <Button variant="outline" size="sm" onClick={fetch} disabled={loading} className="h-9 border-gray-600 text-gray-300">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <ExportButtons title={`Advances vs Salary — last ${months} months`} columns={columns} rows={data.rows} meta={`Risk threshold ${threshold}%`} baseName="advances-vs-salary" />
        </div>
      </CardHeader>
      <CardContent>
        {data.high_risk_count > 0 && (
          <div className="mb-3 px-3 py-2 rounded bg-red-500/10 border border-red-500/30 text-red-200 text-xs flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-400" />
            <span><b>{data.high_risk_count}</b> employee(s) flagged HIGH RISK (advance &gt; {threshold}% of salary in last {months} months).</span>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow className="border-gray-700">
              {columns.map((c) => <TableHead key={c.label} className="text-gray-400 text-xs">{c.label}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows?.length === 0 ? (
              <TableRow><TableCell colSpan={columns.length} className="text-center text-gray-500 py-6">No data</TableCell></TableRow>
            ) : (data.rows || []).map((r, i) => (
              <TableRow key={i} className={`border-gray-800 ${r.high_risk ? "bg-red-500/5" : ""}`} data-testid={`vs-salary-row-${i}`}>
                <TableCell className="text-white flex items-center gap-1">
                  {r.high_risk && <AlertTriangle className="h-3 w-3 text-red-400" />}
                  {r.employee_name}
                </TableCell>
                <TableCell className="text-amber-300 font-mono">₹{r.total_advance.toFixed(2)}</TableCell>
                <TableCell className="text-emerald-300 font-mono">₹{r.total_salary.toFixed(2)}</TableCell>
                <TableCell className={`font-mono font-bold ${r.high_risk ? "text-red-300" : r.ratio_pct == null ? "text-gray-600" : "text-cyan-300"}`}>
                  {r.ratio_pct == null ? "—" : `${r.ratio_pct}%`}
                </TableCell>
                <TableCell>
                  {r.high_risk ? <Badge className="bg-red-500/20 text-red-300">HIGH</Badge> : <span className="text-gray-500 text-xs">OK</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Employee Profile — pick an employee, see absent/half-day/holiday list per
// month with reasons + absent trend chart + pending advances + advance history
// all on one page. (Customer ask, Feb 2026.)
// ---------------------------------------------------------------------------
const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const EmployeeProfile = () => {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [attendance, setAttendance] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/hr/employees`).then(r => {
      const active = (r.data || []).filter(e => e.is_active !== false);
      setEmployees(active);
    }).catch(() => toast.error("Failed to load employees"));
  }, []);

  const loadProfile = async (name, yr) => {
    if (!name) return;
    setLoading(true);
    try {
      const [attRes, advRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/hr/attendance`, { params: { employee_name: name } }),
        axios.get(`${BACKEND_URL}/api/hr/advances`, { params: { employee_name: name } }),
      ]);
      // Filter attendance by selected year
      const yrRecords = (attRes.data || []).filter(r => {
        const recYear = parseInt((r.date || "").split("-")[0], 10);
        return recYear === yr;
      });
      yrRecords.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      setAttendance(yrRecords);
      setAdvances(advRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load profile");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (selected) loadProfile(selected, year); }, [selected, year]);

  // Monthly breakdown of absences (Absent + Half-day + Holiday) with reasons.
  const monthlyAbsenceRows = useMemo(() => {
    const buckets = {}; // m → {absent, half, holiday, reasons: [{date, status, reason}]}
    attendance.forEach(r => {
      if (!r.status || r.status === "Present" || r.status === "Late") return;
      const parts = (r.date || "").split("-");
      const m = parseInt(parts[1], 10);
      if (!buckets[m]) buckets[m] = { absent: 0, half: 0, holiday: 0, leave: 0, items: [] };
      const b = buckets[m];
      if (r.status === "Absent") b.absent++;
      else if (r.status === "Half-day") b.half++;
      else if (r.status === "Holiday") b.holiday++;
      else if (r.status === "Leave") b.leave++;
      b.items.push({ date: r.date, status: r.status, reason: r.absent_reason || "" });
    });
    return MONTH_LABELS.map((lbl, i) => {
      const m = i + 1;
      const b = buckets[m] || { absent: 0, half: 0, holiday: 0, leave: 0, items: [] };
      return { month: lbl, month_num: m, ...b };
    });
  }, [attendance]);

  // Absent trend chart data — last 12 months ending at the selected year-end.
  const trendData = useMemo(() => {
    return monthlyAbsenceRows.map(r => ({
      month: r.month,
      Absent: r.absent,
      "Half-day": r.half,
      Holiday: r.holiday,
    }));
  }, [monthlyAbsenceRows]);

  // Advance buckets
  const pendingAdvances = useMemo(() =>
    advances.filter(a => ["Requested", "Credited", "Pending", "Partially Deducted"].includes(a.status)),
    [advances]);
  const advanceHistory = useMemo(() =>
    [...advances].sort((a, b) => (b.date || "").localeCompare(a.date || "")),
    [advances]);

  const totalAbsent = monthlyAbsenceRows.reduce((s, r) => s + r.absent, 0);
  const totalHalf = monthlyAbsenceRows.reduce((s, r) => s + r.half, 0);
  const totalHoliday = monthlyAbsenceRows.reduce((s, r) => s + r.holiday, 0);
  const totalPendingAmount = pendingAdvances.reduce((s, a) => s + (a.remaining ?? a.amount ?? 0), 0);

  const advanceStatusColor = (s) => {
    if (s === "Requested") return "bg-amber-500/20 text-amber-300";
    if (s === "Credited" || s === "Pending") return "bg-cyan-500/20 text-cyan-300";
    if (s === "Partially Deducted") return "bg-purple-500/20 text-purple-300";
    if (s === "Fully Deducted" || s === "Closed") return "bg-emerald-500/20 text-emerald-300";
    return "bg-gray-500/20 text-gray-300";
  };

  return (
    <Card className="bg-gray-900/60 border-gray-700" data-testid="employee-profile-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <CardTitle className="text-cyan-300 text-base flex items-center gap-2"><UserIcon className="h-4 w-4" /> Employee Profile</CardTitle>
            <p className="text-xs text-gray-400 mt-1">Pick an employee — see month-wise off-days with reasons, trend, pending advances, and advance history.</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-xs text-gray-400">Employee</Label>
              <Select value={selected} onValueChange={setSelected}>
                <SelectTrigger className="bg-gray-800 border-gray-600 text-white h-9 w-60" data-testid="emp-profile-select">
                  <SelectValue placeholder="Select employee..." />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600 max-h-60">
                  {employees.map(e => <SelectItem key={e.name} value={e.name} className="text-white">{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-400">Year</Label>
              <Input type="number" value={year} onChange={e => setYear(parseInt(e.target.value, 10) || year)}
                className="h-9 w-24 bg-gray-800 border-gray-600 text-white" data-testid="emp-profile-year" />
            </div>
            <Button variant="outline" size="sm" disabled={!selected || loading} onClick={() => loadProfile(selected, year)} className="h-9 border-gray-600 text-gray-300">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>
      </CardHeader>

      {!selected ? (
        <CardContent>
          <div className="text-center text-gray-500 py-10">Pick an employee from the dropdown above to view their profile.</div>
        </CardContent>
      ) : (
        <CardContent className="space-y-5">
          {/* Summary chips */}
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-3 py-1.5 rounded border bg-red-500/10 border-red-500/40 text-red-300" data-testid="profile-chip-absent">
              Absent: <b className="ml-1">{totalAbsent}</b>
            </span>
            <span className="px-3 py-1.5 rounded border bg-amber-500/10 border-amber-500/40 text-amber-300" data-testid="profile-chip-half">
              Half-day: <b className="ml-1">{totalHalf}</b>
            </span>
            <span className="px-3 py-1.5 rounded border bg-blue-500/10 border-blue-500/40 text-blue-300" data-testid="profile-chip-holiday">
              Holiday: <b className="ml-1">{totalHoliday}</b>
            </span>
            <span className="px-3 py-1.5 rounded border bg-purple-500/10 border-purple-500/40 text-purple-300" data-testid="profile-chip-pending-advances">
              Pending Advances: <b className="ml-1">{pendingAdvances.length}</b> · ₹{totalPendingAmount.toFixed(2)}
            </span>
          </div>

          {/* Absent trend chart */}
          <div className="bg-gray-950/60 border border-gray-800 rounded p-3">
            <div className="text-xs text-gray-400 mb-2 flex items-center gap-1"><BarChart3 className="h-3.5 w-3.5" /> Off-day Trend — {year}</div>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <BarChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="month" stroke="#9ca3af" style={{ fontSize: 11 }} />
                  <YAxis stroke="#9ca3af" style={{ fontSize: 11 }} allowDecimals={false} />
                  <ReTooltip contentStyle={{ backgroundColor: "#111827", border: "1px solid #374151", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Absent" fill="#ef4444" />
                  <Bar dataKey="Half-day" fill="#f59e0b" />
                  <Bar dataKey="Holiday" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Monthly absent breakdown with reasons */}
          <div>
            <div className="text-xs text-gray-400 mb-2 flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Monthly Off-day Breakdown — {year}</div>
            <div className="border border-gray-800 rounded overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-800 bg-gray-900/50">
                    <TableHead className="text-gray-400 text-xs">Month</TableHead>
                    <TableHead className="text-gray-400 text-xs">Absent</TableHead>
                    <TableHead className="text-gray-400 text-xs">Half</TableHead>
                    <TableHead className="text-gray-400 text-xs">Holiday</TableHead>
                    <TableHead className="text-gray-400 text-xs">Dates &amp; Reasons</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monthlyAbsenceRows.map(r => (
                    <TableRow key={r.month_num} className="border-gray-800" data-testid={`profile-month-row-${r.month_num}`}>
                      <TableCell className="text-white text-xs font-medium">{r.month}</TableCell>
                      <TableCell className={`text-xs font-mono ${r.absent > 0 ? "text-red-300" : "text-gray-600"}`}>{r.absent}</TableCell>
                      <TableCell className={`text-xs font-mono ${r.half > 0 ? "text-amber-300" : "text-gray-600"}`}>{r.half}</TableCell>
                      <TableCell className={`text-xs font-mono ${r.holiday > 0 ? "text-blue-300" : "text-gray-600"}`}>{r.holiday}</TableCell>
                      <TableCell className="text-xs text-gray-300">
                        {r.items.length === 0 ? <span className="text-gray-600">—</span> : (
                          <div className="space-y-0.5">
                            {r.items.map((it, i) => {
                              const day = parseInt((it.date || "").split("-")[2], 10);
                              const badge = it.status === "Absent" ? "bg-red-500/20 text-red-300"
                                          : it.status === "Half-day" ? "bg-amber-500/20 text-amber-300"
                                          : it.status === "Holiday" ? "bg-blue-500/20 text-blue-300"
                                          : "bg-purple-500/20 text-purple-300";
                              return (
                                <div key={i} className="flex items-center gap-2">
                                  <span className="text-gray-400 font-mono w-7">{String(day).padStart(2, "0")}</span>
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${badge}`}>{it.status}</span>
                                  <span className="text-gray-200">{it.reason || <span className="text-gray-600 italic">no reason</span>}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Pending advances */}
          <div>
            <div className="text-xs text-gray-400 mb-2 flex items-center gap-1"><Wallet className="h-3.5 w-3.5" /> Pending Advances ({pendingAdvances.length})</div>
            <div className="border border-gray-800 rounded overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-800 bg-gray-900/50">
                    <TableHead className="text-gray-400 text-xs">Date</TableHead>
                    <TableHead className="text-gray-400 text-xs">Amount</TableHead>
                    <TableHead className="text-gray-400 text-xs">Deducted</TableHead>
                    <TableHead className="text-gray-400 text-xs">Remaining</TableHead>
                    <TableHead className="text-gray-400 text-xs">EMI</TableHead>
                    <TableHead className="text-gray-400 text-xs">Reason</TableHead>
                    <TableHead className="text-gray-400 text-xs">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingAdvances.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-gray-500 py-4 text-xs">No pending advances — all settled.</TableCell></TableRow>
                  ) : pendingAdvances.map((a, i) => (
                    <TableRow key={a.id || i} className="border-gray-800" data-testid={`profile-pending-row-${i}`}>
                      <TableCell className="text-white text-xs font-mono">{a.date}</TableCell>
                      <TableCell className="text-amber-300 text-xs font-mono">₹{(a.amount || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-emerald-300 text-xs font-mono">₹{(a.deducted_total || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-red-300 text-xs font-mono font-bold">₹{(a.remaining ?? a.amount ?? 0).toFixed(2)}</TableCell>
                      <TableCell className="text-cyan-300 text-xs font-mono">₹{(a.per_installment || 0).toFixed(2)} × {a.installments || 1}</TableCell>
                      <TableCell className="text-gray-300 text-xs">{a.reason || ""}</TableCell>
                      <TableCell><Badge className={advanceStatusColor(a.status)}>{a.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Advance history (all advances + deduction history) */}
          <div>
            <div className="text-xs text-gray-400 mb-2 flex items-center gap-1"><History className="h-3.5 w-3.5" /> Advance History — full timeline ({advanceHistory.length})</div>
            <div className="border border-gray-800 rounded overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-800 bg-gray-900/50">
                    <TableHead className="text-gray-400 text-xs">Date</TableHead>
                    <TableHead className="text-gray-400 text-xs">Amount</TableHead>
                    <TableHead className="text-gray-400 text-xs">Reason</TableHead>
                    <TableHead className="text-gray-400 text-xs">Deductions</TableHead>
                    <TableHead className="text-gray-400 text-xs">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {advanceHistory.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-4 text-xs">No advances on record.</TableCell></TableRow>
                  ) : advanceHistory.map((a, i) => (
                    <TableRow key={a.id || i} className="border-gray-800" data-testid={`profile-history-row-${i}`}>
                      <TableCell className="text-white text-xs font-mono">{a.date}</TableCell>
                      <TableCell className="text-amber-300 text-xs font-mono">₹{(a.amount || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-gray-300 text-xs">{a.reason || ""}</TableCell>
                      <TableCell className="text-xs text-gray-200">
                        {(a.history || []).length === 0 ? <span className="text-gray-600">—</span> : (
                          <div className="space-y-0.5">
                            {(a.history || []).map((h, j) => (
                              <div key={j} className="text-[11px] text-gray-400">
                                {h.month}/{h.year}: <span className="text-emerald-300">₹{(h.amount || 0).toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell><Badge className={advanceStatusColor(a.status)}>{a.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// HRReports root — tabbed Attendance / Advances
// ---------------------------------------------------------------------------
const HRReports = () => {
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><BarChart3 className="h-6 w-6 text-amber-400" /> HR Reports</h1>
        <p className="text-sm text-gray-400">Attendance &amp; Advances analytics for review and audit.</p>
      </div>
      <Tabs defaultValue="employee-profile">
        <TabsList className="bg-gray-900 border border-gray-700">
          <TabsTrigger value="employee-profile" data-testid="hr-reports-tab-profile">Employee Profile</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="hr-reports-tab-attendance">Attendance (3)</TabsTrigger>
          <TabsTrigger value="advances" data-testid="hr-reports-tab-advances">Advances (3)</TabsTrigger>
        </TabsList>
        <TabsContent value="employee-profile" className="space-y-5 pt-4">
          <EmployeeProfile />
        </TabsContent>
        <TabsContent value="attendance" className="space-y-5 pt-4">
          <AnnualAttendanceSummary />
          <MonthlyAttendanceSheet />
          <AttendanceTrends />
        </TabsContent>
        <TabsContent value="advances" className="space-y-5 pt-4">
          <OutstandingAdvances />
          <AdvanceHistory />
          <AdvancesVsSalary />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HRReports;
